import React from 'react';
import { Phone, Clock, Building2 } from 'lucide-react';
import { une, institutionalLinks } from '../../content';

// Valores por defecto
const defaultUne = {
  sectionLabel: "UNE",
  sectionTitle: "Unidad Especializada de Atención a Usuarios",
  description: "En caso de alguna consulta, reclamación o aclaración, podrá presentarla en la UNE.",
  titular: { title: "Titular de la Unidad", name: "Nombre del titular" },
  schedule: "Lunes a viernes de 9:00 am a 6:00 pm",
  phone: "(656) 536-7364",
  phoneLink: "tel:6565367364"
};

export const UNESection = () => {
  // Obtener contenido con fallbacks
  const content = {
    sectionLabel: une?.sectionLabel || defaultUne.sectionLabel,
    sectionTitle: une?.sectionTitle || defaultUne.sectionTitle,
    description: une?.description || defaultUne.description,
    titular: une?.titular || defaultUne.titular,
    schedule: une?.schedule || defaultUne.schedule,
    phone: une?.phone || defaultUne.phone,
    phoneLink: une?.phoneLink || defaultUne.phoneLink
  };

  const links = {
    buro: institutionalLinks?.buro || { name: "Buró de Entidades Financieras", url: "#", logo: "/images/logo-buro.png" },
    condusef: institutionalLinks?.condusef || { name: "CONDUSEF", url: "#", logo: "/images/logo-condusef.png" }
  };

  return (
    <section id="une" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#CFA935] font-semibold text-sm uppercase tracking-wider">
            {content.sectionLabel}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-[#013C5D]">
            {content.sectionTitle}
          </h2>
          <p className="mt-4 text-gray-600 text-lg">
            {content.description}
          </p>
        </div>

        {/* UNE Info Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#013C5D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-[#013C5D]" />
              </div>
              <h3 className="text-lg font-semibold text-[#CFA935] uppercase tracking-wide">
                {content.titular.title}
              </h3>
              <p className="text-2xl font-bold text-[#013C5D] mt-2">
                {content.titular.name}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-[#CFA935]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#CFA935]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#013C5D]">Horario de atención</h4>
                  <p className="text-gray-600">{content.schedule}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 bg-[#CFA935]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#CFA935]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#013C5D]">Teléfono</h4>
                  <a href={content.phoneLink} className="text-gray-600 hover:text-[#CFA935] transition-colors">
                    {content.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ligas de interés */}
        <div className="mt-16">
          <h3 className="text-center text-xl font-bold text-[#013C5D] mb-8">
            Ligas de interés
          </h3>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <a
              href={links.buro.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-[#CFA935] hover:shadow-md transition-all group"
            >
              <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img 
                  src={links.buro.logo} 
                  alt={links.buro.name}
                  className="w-14 h-14 object-contain"
                />
              </div>
              <div>
                <h4 className="font-semibold text-[#013C5D] group-hover:text-[#CFA935] transition-colors">
                  {links.buro.name}
                </h4>
                <p className="text-sm text-gray-500">buro.gob.mx</p>
              </div>
            </a>

            <a
              href={links.condusef.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-[#CFA935] hover:shadow-md transition-all group"
            >
              <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img 
                  src={links.condusef.logo} 
                  alt={links.condusef.name}
                  className="w-14 h-14 object-contain"
                />
              </div>
              <div>
                <h4 className="font-semibold text-[#013C5D] group-hover:text-[#CFA935] transition-colors">
                  {links.condusef.name}
                </h4>
                <p className="text-sm text-gray-500">condusef.gob.mx</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
